import React, { Component, Fragment } from "react";
import { IntlProvider } from "react-intl";

import Authentification from "./Authentification";
import Welcome from "./Welcome";
import Inscription from "./Inscription";
import Thanks from "./Thanks";
import AppConfig from "./AppConfig";

import { createInscription } from "./services/clubmed";
import {
  fetchActivities,
  fetchLevels,
  fectchProducts,
  fetchPrestations,
  validation,
} from "./services/api";
import { formatDate, filterProducts } from "./services/utils";

import frMessages from "./messages/fr.json";
import enMessages from "./messages/en.json";

const UNDEF = undefined;

const clearError = { technicalError: UNDEF, doublon: UNDEF };
const clearLanguage = { language: UNDEF, languages: UNDEF, ...clearError };
const clearMeeting = {
  meeting: UNDEF,
  meetings: UNDEF,
  prestationCode: UNDEF,
  ...clearLanguage,
};
const clearProduct = { product: UNDEF, products: UNDEF, ...clearMeeting };
const clearLevel = { level: UNDEF, levels: UNDEF, ...clearProduct };
const clearActivity = { activity: UNDEF, activities: UNDEF, ...clearLevel };

const stateShape = {
  step: -1,
  displayConfig: false,
  lang: "fr",
  welcome: {},
  validating: false,
  identification: {
    firstname: { value: "", error: undefined },
    lastname: { value: "", error: undefined },
    room: { value: "", error: undefined },
    phone: { value: "", error: undefined },
  },
};

const initState = {
  ...stateShape,
  stayFrom: undefined,
  stayTo: undefined,
  birthday: undefined,
  ageRanges: undefined,
  doublon: undefined,
  technicalError: undefined,
  ...clearActivity,
};

const resetState = {
  ...initState,
  token: undefined,
  resort: undefined,
  school: undefined,
  intermediary: undefined,
  lang: undefined,
  minDuration: undefined,
  maxDuration: undefined,
  doublon: undefined,
  technicalError: undefined,
  step: 0,
};

const mergeValidation = (state) => ({ step: 3 });
const mergeValidating = (validating) => (state) => ({ validating });
const mergeIdentification = (identification) => (state) => ({ identification });
const mergeTechnicalError = (technicalError) => (state) => ({ technicalError });
const mergeAuthenticated =
  (token, resort, school, intermediary) => (state) => ({
    token,
    resort,
    school,
    intermediary,
    step: 1,
  });
const mergeAgeRanges = (ageRanges) => (state) => ({ ageRanges, ...clearError });
const mergeTextfield = (name, value, error) => (state) => ({
  identification: { ...state.identification, [name]: { value, error } },
});
const mergeLanguages = (languages) => (state) => ({ languages });
const mergeLanguage = (language) => (state) => ({ language });
const mergeMeetings = (meetings) => (state) => ({
  ...meetings,
  ...clearLanguage,
});
const mergeMeeting = (meeting) => (state) => ({ meeting, ...clearLanguage });
const mergeProducts = (products) => (state) => ({ products, ...clearMeeting });
const mergeProduct = (product) => (state) => ({ product, ...clearMeeting });
const mergeLevels = (levels) => (state) => ({ levels, ...clearProduct });
const mergeLevel = (level) => (state) => ({ level, ...clearProduct });
const mergeActivities = (activities) => (state) => ({
  activities,
  ...clearLevel,
});
const mergeActivity = (activity) => (state) => ({ activity, ...clearLevel });
const mergeBirthday = (birthday) => (state) => ({ birthday, ...clearActivity });
const mergeWelcome = (welcome) => (state) => ({ welcome });
const mergeStep = (step) => (state) => ({ step });
const mergeWelcomeAndLang = (infos, lang) => (state) => {
  const { error, season, stayFrom, stayTo } = infos;
  const { minDuration, maxDuration } = infos;

  return {
    lang,
    welcome: { error, season },
    stayFrom,
    stayTo,
    minDuration,
    maxDuration,
    step: lang !== undefined ? 2 : state.step,
  };
};
const mergeDoublon = (doublon) => (state) => ({ doublon });

const removeToken = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenStamp");
};

const readToken = () => {
  const { token, tokenStamp } = localStorage;
  if (token !== undefined && tokenStamp !== undefined) {
    const stamp = new Date(tokenStamp);
    const month = stamp.getMonth() + 1;
    const year = stamp.getFullYear();
    const refYear = month < 9 ? year : year + 1;
    const refDate = new Date(refYear, 8, 1);
    return new Date() < refDate ? token : removeToken();
  }
};

const writeToken = (token) => {
  localStorage.token = token;
  localStorage.tokenStamp = formatDate(new Date());
};

export default class App extends Component {
  state = { ...stateShape };

  componentDidMount() {
    const token = readToken();
    if (token !== undefined) {
      return this.authenticated(token);
    } else {
      this.setState(mergeStep(0));
    }
  }

  authenticated = (token) => {
    const data = JSON.parse(atob(token.split(".")[1]));
    const { code: intermediary, merchant } = data;
    const resort = merchant.code.split("-")[1];
    const school = merchant.libelle;

    writeToken(token);
    this.setState(mergeAuthenticated(token, resort, school, intermediary));
  };

  newInscription = () => {
    const token = readToken();
    this.setState(token === undefined ? initState : { ...initState, step: 1 });
  };

  navToConfig = () => {
    const { intermediary, resort, lang } = this.state;
    createInscription(intermediary, resort, "fr").then((infos) => {
      this.setState(mergeWelcomeAndLang(infos, lang), () => {
        this.setState({ displayConfig: true });
      });
    });
  };

  closeConfig = () => {
    this.setState({ displayConfig: false });
  };

  startNewInscription = (lang) => () => {
    const { intermediary, resort } = this.state;
    this.setState(mergeWelcome({}));
    createInscription(intermediary, resort, lang).then((infos) => {
      this.setState(mergeWelcomeAndLang(infos, lang));
    });
  };

  ageRangesUpdated = (ageRanges) => {
    const { error } = ageRanges;
    this.setState(
      error !== undefined
        ? mergeTechnicalError(error)
        : mergeAgeRanges(ageRanges)
    );
  };

  saveConfig = (startDate, endDate, duration) => {
    const { lang } = this.state;

    if (startDate === undefined || startDate.trim() === "") {
      localStorage.removeItem("startDate");
      localStorage.removeItem("endDate");
    } else {
      localStorage.startDate = startDate;
      localStorage.endDate = endDate;
    }

    localStorage.minDuration = duration.min;
    localStorage.maxDuration = duration.max;

    this.setState({ displayConfig: false }, () => {
      if (lang !== undefined) {
        this.startNewInscription(lang)();
      }
    });
  };

  reset = () => {
    localStorage.clear();
    this.setState(resetState);
  };

  handleChoices = ({
    error,
    choices,
    code,
    merge,
    changed,
    extract = (xs) => xs.map((x) => x.code),
    isAutoSelected = (xs) => xs !== undefined && xs.length === 1,
  }) => {
    if (error !== undefined) {
      return this.setState(mergeTechnicalError(error));
    }

    this.setState(merge(choices), () => {
      const extracted = choices !== undefined ? extract(choices) : undefined;
      const validSelection =
        code !== undefined &&
        extracted !== undefined &&
        extracted.includes(code);

      const selected = validSelection
        ? code
        : isAutoSelected(extracted)
        ? extracted[0]
        : undefined;

      if (selected !== undefined) {
        changed(selected);
      }
    });
  };

  textfieldChanged = (name, value) => {
    this.setState(mergeTextfield(name, value));
  };

  birthDateChanged = (year, month, date) => {
    const {
      intermediary,
      resort,
      lang,
      welcome: { season },
      stayFrom,
      stayTo,
      activity,
    } = this.state;
    const birthday =
      year !== undefined && month !== undefined && date !== undefined
        ? formatDate(new Date(year, month, date))
        : undefined;

    this.setState(mergeBirthday(birthday));

    if (birthday === undefined) {
      return;
    }

    fetchActivities(
      intermediary,
      resort,
      lang,
      season,
      stayFrom,
      stayTo,
      birthday
    ).then((result) => {
      const { choices, error } = result;
      const err = error && error === "Invalid kind" ? "noKind" : error;
      this.handleChoices({
        error: err,
        choices: choices,
        code: activity,
        merge: mergeActivities,
        changed: this.activityChanged,
      });
    });
  };

  activityChanged = (activity) => {
    const {
      intermediary,
      resort,
      lang,
      welcome: { season },
      stayFrom,
      stayTo,
      birthday,
      level,
    } = this.state;

    this.setState(mergeActivity(activity));

    fetchLevels(
      intermediary,
      resort,
      lang,
      season,
      stayFrom,
      stayTo,
      birthday,
      activity
    ).then((result) => {
      const { choices, error } = result;
      this.handleChoices({
        error,
        choices,
        code: level,
        merge: mergeLevels,
        changed: this.levelChanged,
      });
    });
  };

  levelChanged = (level) => {
    const {
      intermediary,
      resort,
      lang,
      welcome: { season },
      stayFrom,
      stayTo,
      birthday,
      activity,
      product,
      minDuration,
      maxDuration,
    } = this.state;

    this.setState(mergeLevel(level));

    fectchProducts(
      intermediary,
      resort,
      lang,
      season,
      stayFrom,
      stayTo,
      birthday,
      activity,
      level
    ).then((result) => {
      const { choices, error } = result;
      this.handleChoices({
        error: error,
        choices: filterProducts(choices, minDuration, maxDuration),
        code: product,
        merge: mergeProducts,
        changed: this.productChanged,
        extract: (xs) =>
          [].concat(...xs.map((x) => x.products.map((p) => p.code))),
      });
    });
  };

  productChanged = (product) => {
    const {
      intermediary,
      resort,
      lang,
      welcome: { season },
      stayFrom,
      stayTo,
      birthday,
      activity,
      level,
      meeting,
    } = this.state;

    this.setState(mergeProduct(product));

    fetchPrestations(
      intermediary,
      resort,
      lang,
      season,
      stayFrom,
      stayTo,
      birthday,
      activity,
      level,
      product
    ).then((result) => {
      const { choices, error } = result;

      const meetings =
        choices !== undefined && choices.length > 0
          ? { meetings: choices[0].choices, prestationCode: choices[0].code }
          : undefined;

      this.handleChoices({
        error: error,
        choices: meetings,
        code: meeting,
        merge: mergeMeetings,
        changed: this.meetingChanged,
        extract: (xs) => xs.meetings.map((p) => p.code),
      });
    });
  };

  meetingChanged = (meeting) => {
    const { meetings, language } = this.state;
    this.setState(mergeMeeting(meeting), () => {
      const languages = meetings.filter((m) => m.code === meeting)[0].languages;

      this.handleChoices({
        choices: languages,
        code: language,
        merge: mergeLanguages,
        changed: this.languageChanged,
      });
    });
  };

  validateField = (name) => {
    const { identification } = this.state;
    const value = identification[name].value;
    return value.trim().length === 0
      ? { value, error: "mandatory" }
      : { value };
  };

  validateAllFields = () => {
    const { identification } = this.state;
    return Object.keys(identification).reduce(
      (acc, name) => ({ ...acc, [name]: this.validateField(name) }),
      {}
    );
  };

  areFieldsValid = () => {
    const { identification } = this.state;
    return Object.keys(identification).reduce(
      (acc, name) => acc && identification[name].error === undefined,
      true
    );
  };

  getFieldsValue = () => {
    const { identification } = this.state;
    return Object.keys(identification).reduce(
      (acc, name) => ({ ...acc, [name]: identification[name].value }),
      {}
    );
  };

  validation = () => {
    const {
      token,
      intermediary,
      resort,
      lang,
      welcome: { season },
      stayFrom,
      stayTo,
      birthday,
      activity,
      level,
      product,
      prestationCode,
      meeting,
      language,
    } = this.state;

    const fieldsWithErrors = this.validateAllFields();
    this.setState(mergeIdentification(fieldsWithErrors), () => {
      const areFieldsValid = this.areFieldsValid();
      if (areFieldsValid) {
        const { firstname, lastname, room, phone } = this.getFieldsValue();

        this.setState(mergeValidating(true));
        validation(
          token,
          intermediary,
          resort,
          lang,
          season,
          stayFrom,
          stayTo,
          birthday,
          activity,
          level,
          product,
          prestationCode,
          meeting,
          language,
          firstname,
          lastname,
          room,
          phone
        ).then((result) => {
          this.setState(mergeValidating(false));
          const { error, message } = result;
          if (error !== undefined) {
            return this.setState(mergeTechnicalError(error));
          }
          if (message === "Doublon") {
            return this.setState(mergeDoublon(true));
          }
          this.setState(mergeValidation());
        });
      }
    });
  };

  languageChanged = (language) => {
    this.setState(mergeLanguage(language));
  };

  renderAuthentification() {
    return <Authentification authenticated={this.authenticated} />;
  }

  renderWelcome() {
    const { resort, school, startDate } = this.state;
    return (
      <IntlProvider locale="fr" messages={frMessages}>
        <Fragment>
          <Welcome
            resort={resort}
            school={school}
            startDate={startDate}
            startNewInscription={this.startNewInscription}
            navToConfig={this.navToConfig}
          />
          {this.renderAppConfig()}
        </Fragment>
      </IntlProvider>
    );
  }

  renderAppConfig() {
    const { displayConfig, stayFrom, stayTo } = this.state;
    const { minDuration, maxDuration } = this.state;

    if (displayConfig) {
      return (
        <AppConfig
          stayFrom={stayFrom}
          stayTo={stayTo}
          duration={{
            min: minDuration,
            max: maxDuration,
          }}
          manageStay={localStorage.startDate !== undefined}
          close={this.closeConfig}
          saveConfig={this.saveConfig}
          reset={this.reset}
        />
      );
    }
  }

  renderInscription() {
    const {
      intermediary,
      resort,
      lang,
      school,
      stayFrom,
      stayTo,
      welcome: { season },
      ageRanges,
      identification,
      birthday,
      activities,
      activity,
      levels,
      level,
      products,
      product,
      meetings,
      meeting,
      languages,
      language,
      validating,
      technicalError,
      doublon,
    } = this.state;
    const messages = lang === "fr" ? frMessages : enMessages;
    return (
      <IntlProvider locale={lang} messages={messages}>
        <Fragment>
          <Inscription
            intermediary={intermediary}
            resort={resort}
            lang={lang}
            school={school}
            season={season}
            stayFrom={stayFrom}
            stayTo={stayTo}
            ageRanges={ageRanges}
            identification={identification}
            birthday={birthday}
            activities={activities}
            activity={activity}
            levels={levels}
            level={level}
            products={products}
            product={product}
            meetings={meetings}
            meeting={meeting}
            languages={languages}
            language={language}
            validating={validating}
            technicalError={technicalError}
            doublon={doublon}
            startNewInscription={this.startNewInscription}
            newInscription={this.newInscription}
            ageRangesUpdated={this.ageRangesUpdated}
            textfieldChanged={this.textfieldChanged}
            birthDateChanged={this.birthDateChanged}
            activityChanged={this.activityChanged}
            levelChanged={this.levelChanged}
            productChanged={this.productChanged}
            meetingChanged={this.meetingChanged}
            languageChanged={this.languageChanged}
            validation={this.validation}
            navToConfig={this.navToConfig}
          />
          {this.renderAppConfig()}
        </Fragment>
      </IntlProvider>
    );
  }

  renderThanks() {
    const { lang, school } = this.state;
    const messages = lang === "fr" ? frMessages : enMessages;
    return (
      <IntlProvider locale={lang} messages={messages}>
        <Thanks newInscription={this.newInscription} school={school} />
      </IntlProvider>
    );
  }

  renderStep() {
    const { step } = this.state;
    if (step === 0) return this.renderAuthentification();
    if (step === 1) return this.renderWelcome();
    if (step === 2) return this.renderInscription();
    if (step === 3) return this.renderThanks();
  }

  render() {
    return <div className="App">{this.renderStep()}</div>;
  }
}



// WEBPACK FOOTER //
// ./src/App.js